import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { GET_CUSTOMER_DETAILS, GET_PROFILE } from 'store/queries';
import moment from 'moment';

import LanguageSelector from 'components/ui/LanguageSelector';
import { Column } from 'components/shared/Layout';
import HideWhenPrinted from 'components/shared/HideWhenPrinted';

import { userShape } from 'constants/propTypes';

import { Html5QrcodeScanner } from 'html5-qrcode';
import { MOBILE_TERMINAL_ID_STORAGE_KEY, MOBILE_TERMINAL_SERIAL_NO_STORAGE_KEY } from 'constants/user';
import Logout from './Logout';
import { StyledHeader, StyledImage, StyledLink, StyledButton, LoyaltyAccountNumberLink } from './Header.styles';
import Modal from '../../ui/Modal';
import { SubscriptionCodeTitle } from 'constants/subscriptions';

import LogoNew from './voyager-logo-new.svg';

const Header = ({ user }) => {
  const [isNameMasked, setIsNameMasked] = useState(false);
  const [shouldScanQR, setShouldScanQR] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const scannerRef = useRef(null);

  const history = useHistory();
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const subscriptionID = new URLSearchParams(history.location.search).get('subscriptionID');

  const [getCustomerInfo, { data }] = useLazyQuery(GET_CUSTOMER_DETAILS, {
    variables: {
      membershipNumber
    },
    fetchPolicy: 'network-only'
  });
  const [getProfile, { data: profileData }] = useLazyQuery(GET_PROFILE);

  const userFullName = user?.fullName ?? '';

  const toggleNameMask = useCallback(() => setIsNameMasked((prevState) => !prevState), []);

  const maskedName = useMemo(() => userFullName.replace(/[^\s]/gim, '*'), [userFullName]);

  useEffect(() => {
    if (!membershipNumber) {
      return;
    }

    getCustomerInfo();
  }, [membershipNumber, getCustomerInfo]);

  const customerInfo = data && data.getCustomerDetails;

  useEffect(() => {
    if (!customerInfo) {
      return;
    }

    getProfile({
      variables: {
        customerId: customerInfo?.profile?.customerId,
        subscriptionSearchType: 'CURRENT'
      }
    });
  }, [customerInfo, customerInfo?.profile?.customerId, getProfile]);

  useEffect(() => {
    if (!profileData || !subscriptionID) {
      return;
    }

    setSubscription(profileData.profile.subscriptions.find((s) => s.travelPassReference === subscriptionID));

    return () => {
      setSubscription(null);
    };
  }, [profileData, subscriptionID, setSubscription]);

  const toggleModal = () => {
    setShouldScanQR(!shouldScanQR);
    setScanLoading(!shouldScanQR);
  };

  const getConnectedMobileTerminalId = localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY);

  const scan = () => {
    toggleModal();
  };

  const setTerminalId = (terminalId) => {
    if (!terminalId) {
      return;
    }

    const sepIdx = terminalId.indexOf('-');
    const serialNo = terminalId.substring(sepIdx + 1);

    localStorage.setItem(MOBILE_TERMINAL_ID_STORAGE_KEY, terminalId);
    localStorage.setItem(MOBILE_TERMINAL_SERIAL_NO_STORAGE_KEY, serialNo);
  };

  useEffect(() => {
    if (shouldScanQR) {
      scannerRef.current = new Html5QrcodeScanner('qr-reader-container', { fps: 10, qrbox: 250, aspectRatio: 1.0 }, /* verbose= */ false);

      scannerRef.current.render(
        (decodedText) => {
          try {
            const result = JSON.parse(decodedText);
            setTerminalId(result?.terminal);
            setShouldScanQR(false);
            setScanLoading(false);
            // Очищаем сканер
            if (scannerRef.current) {
              scannerRef.current.clear();
            }
          } catch (error) {
            console.error('Invalid QR code format:', error);
          }
        },
        (errorMessage) => {}
      );
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, [shouldScanQR]);

  return (
    <>
      <Modal isOpen={shouldScanQR} title="Scan QR Code" onClose={toggleModal}>
        <div id="qr-reader-container" style={{ width: '100%' }}></div>
      </Modal>
      <HideWhenPrinted>
        <StyledHeader>
          <Column span={3}>
            <StyledLink to="/">
              <StyledImage src={LogoNew} id="homepage-link" alt="Voyager Logo" />
            </StyledLink>
          </Column>

          {membershipNumber && (
            <Column start={6} span={5} justifyContent="center" alignItems="center">
              <LoyaltyAccountNumberLink to={`/clubeurostar/${membershipNumber}`}>
                <FormattedMessage id="clubEurostar.usingLoyaltyAccount" />
                {' - '}
                {membershipNumber}

                {subscription &&
                  ` with ${SubscriptionCodeTitle[subscription.code]}(${moment(subscription.startDate).format('DD/MM/YYYY')} - ${moment(
                    subscription.endDate
                  ).format('DD/MM/YYYY')})`}
              </LoyaltyAccountNumberLink>
            </Column>
          )}

          {user && user.isStation && (
            <Column start={11} span={5} justifyContent="flex-end" alignItems="center">
              <StyledButton loading={scanLoading} onClick={scan} disabled={scanLoading} data-cy="terminal-scan-button">
                {getConnectedMobileTerminalId ? (
                  <>
                    <FormattedMessage id="mobileTerminal.button.id" />
                    {' - '}
                    {getConnectedMobileTerminalId}
                  </>
                ) : (
                  <FormattedMessage id="mobileTerminal.button.connect" />
                )}
              </StyledButton>
            </Column>
          )}

          {user && (
            <Column start={16} span={2} justifyContent="flex-end" alignItems="center" onClick={toggleNameMask} cursor="pointer">
              {isNameMasked ? maskedName : userFullName}
            </Column>
          )}

          {user && (
            <Column start={18} span={1} justifyContent="flex-end" alignItems="center">
              <Logout key="logout-button" />
            </Column>
          )}

          <Column start={19} span={2} justifyContent="flex-end" alignItems="center">
            <LanguageSelector key="language-selector-button" />
          </Column>
        </StyledHeader>
      </HideWhenPrinted>
    </>
  );
};

Header.propTypes = {
  user: userShape
};

export default Header;
